// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import createCache from "@emotion/cache";
import {
  CssBaseline,
  ThemeProvider as ThemeProviderWrapper,
  Direction,
} from "@mui/material";
import { CacheProvider } from "@emotion/react";

// Utils
import { setupTheme } from "src/utils/theme/themeHelper";

// Contexts
import { useThemeModeContext } from "src/contexts/theme/ThemeModeContext";

// Config
import { priorityLanguageMap } from "src/config/languages";

// Create cache once, hoisted outside of the provider
const cache = createCache({
  key: "muirtl",
});

const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const { i18n } = useTranslation();
  const { mode } = useThemeModeContext();

  const languageKey = i18n.language as keyof typeof priorityLanguageMap;
  const languageDir = (priorityLanguageMap[languageKey]?.dir ??
    "ltr") as Direction;

  // only re-render components when direction/language/mode actually change, not on every
  // unrelated render of this provider.
  const theme = useMemo(
    () => setupTheme(languageDir, languageKey, mode),
    [languageDir, languageKey, mode]
  );

  return (
    <CacheProvider value={cache}>
      <ThemeProviderWrapper theme={theme}>
        <div dir={languageDir}>
          <CssBaseline />
          {children}
        </div>
      </ThemeProviderWrapper>
    </CacheProvider>
  );
};

export default ThemeProvider;
